import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {User} from '../models/user.model';
import * as _ from 'lodash';
import * as moment from 'moment';
import {UrlService} from './url.service';

@Injectable()
export class UserService {
    private readonly BASE_URL: string;

    constructor(private http: HttpClient,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/v2/users';
    }

    listUsers(params): Observable<Array<User>> {
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL, requestOptions).pipe(
            map((users: any) => {
                const userList: Array<User> = [];
                if (users) {
                    users.forEach((user) => {
                        userList.push(new User(user));
                    })
                }
                return userList;
            }));
    }

    listUsersFromAll(params): Observable<Array<User>> {
        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/all', requestOptions).pipe(
            map((users: any) => {
                const userList: Array<User> = [];
                if (users) {
                    users.forEach((user) => {
                        userList.push(new User(user));
                    })
                }
                return userList;
            }));
    }

    getUser(userId: string, loadSecurityDetails: boolean): Observable<User> {
        let params: HttpParams = new HttpParams();

        if (!!loadSecurityDetails) {
            params = params.append('loadSecurityDetails', loadSecurityDetails);
        }

        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/' + userId, requestOptions).pipe(
            map((user: any) => {
                return new User(user);
            })
        );
    }

    getLoggedInUser(): Observable<any> {
        return this.http.get(this.BASE_URL + '/loggedIn');
    }

    // Using get user Checklist service endpoint
    listAssignedChecklists(loggedInUserId): Observable<any> {
        return this.http.get(this.BASE_URL + '/' + loggedInUserId + '/checklists').pipe();
    }

    getChecklist(checklistId, loggedInUserId): Observable<any> {
        return this.http.get(this.BASE_URL + '/' + loggedInUserId + '/checklists' + checklistId).pipe();
    }

    getClientPlacementsUserRespFor(userId, endDateCutoff) {
        let params = new HttpParams();
        params = params.set('endDateCutoff', endDateCutoff);
        params = params.set('identifierMatch', false);

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + userId + '/clientPlacements', requestOptions);
    }

    changePassword(newPasswordInfo): Observable<any> {
        console.log('Making password change request to:', this.BASE_URL + '/changePassword');
        console.log('Request payload:', newPasswordInfo);
        
        return this.http.post(this.BASE_URL + '/changePassword', newPasswordInfo).pipe(
            map((user: any) => {
                console.log('Password change response received:', user);
                return user;
            })
        );
    }

    hasPermission(permission, loggedInUser) {
        if (!loggedInUser) {
            return false;
        }

        if (loggedInUser.isSuperAdmin) {
            return true;
        }
        let ret = false;
        loggedInUser.permissions.forEach((value, key) => {
            if (value === permission) {
                ret = true;
                return;
            }
        });
        return ret;
    }

    userIsOuResponsible(ou, loggedInUser) {
        if (ou == null) {
            return false;
        }
        if (loggedInUser != null) {
            if (loggedInUser.isSuperAdmin) {
                return true;
            }
            let ret = false;
            loggedInUser.ouResponsibilities.forEach((resp) => {
                if (ou.id === resp.id) {
                    ret = true;
                    return;
                }
            });
            return ret;
        }

        return false;
    }

    userIsClientResponsible(client, loggedInUser) {
        if (client == null) {
            return false;
        }
        if (loggedInUser != null) {
            if (loggedInUser.isSuperAdmin) {
                return true;
            }
            let ret = false;
            loggedInUser.clientIdsResponsibilities.forEach((resp) => {
                if (client.id === resp) {
                    ret = true;
                    return;
                }
            });
            return ret;
        }

        return false;
    }

    isUserCertified(user, instrumentType, assessmentDate, expGracePeriodDays) {
        // TODO: research whether this isSuperAdmin bypass is needed. Certification should
        // be based on the assessor, not on super-admin status. user.noCertification already
        // provides a per-user opt-out for users who legitimately don't need certification.
        if (user.isSuperAdmin) {
            return true;
        }
        if (user.noCertification) {
            return true;
        }
        if (!assessmentDate) {
            return false;
        }
        const assessmentMoment = moment(assessmentDate);
        let found = false;
        _.every(user.certifications, (certification) => {
            if (certification.type === instrumentType || (certification.type + '_CAREGIVER') === instrumentType) {
                if (moment(certification.certificationDate) <= assessmentMoment
                    && (certification.expirationDate == null
                        || moment(certification.expirationDate).add(expGracePeriodDays, 'days') >= assessmentMoment)) {
                    found = true;
                    return false;
                }
            }
            return true;
        });
        return found;
    }

    canAccessAdmin(loggedInUser) {
        if (!loggedInUser) {
            return false;
        }

        if (loggedInUser.isSuperAdmin) {
            return true;
        }

        if (loggedInUser.hasAdminAccess) {
            return true;
        }

        return false
    }
}
